import { useEffect, useState } from "react";
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
} from "@chakra-ui/react";
import {
  Contato,
  ContatoTemperaturaEnum,
  ParteContatoCobranca,
} from "@/models/consignado/proposal-billings.model";
import { phoneFormatter } from "@/utils/formatPhone";
import { validateAndFormatPhone } from "@/utils/phoneChange";

// Formulário de contato do tomador / empregador (seção "Dados de Contato",
// HIS-024 / TAR-049). Serve pra criar e pra editar: com `contato` preenche os
// campos e o botão vira "Salvar". Os campos são os que o backend aceita no
// POST /contatos e no PUT /contatos/:id (AtualizarContatoDto): nome (só
// empregador — é o contato do RH), telefone, e-mail e temperatura.

export type ContatoFormValues = {
  nome: string;
  telefone: string;
  email: string;
  temperatura: ContatoTemperaturaEnum;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  parte: ParteContatoCobranca;
  // Presente = edição.
  contato?: Contato | null;
  onSubmit: (values: ContatoFormValues) => Promise<unknown>;
  isSaving: boolean;
};

const TELEFONE_MASCARADO_LEN = 15; // "(11) 99999-9999"

const valoresIniciais = (contato?: Contato | null): ContatoFormValues => ({
  nome: contato?.nome ?? "",
  telefone: contato?.telefone ? phoneFormatter(contato.telefone) : "",
  email: contato?.email ?? "",
  temperatura: contato?.temperatura ?? ContatoTemperaturaEnum.QUENTE,
});

export default function ContatoFormModal({
  isOpen,
  onClose,
  parte,
  contato,
  onSubmit,
  isSaving,
}: Props) {
  const [values, setValues] = useState<ContatoFormValues>(() => valoresIniciais(contato));
  const edicao = !!contato;
  const rotuloParte = parte === "tomador" ? "tomador" : "empregador";

  useEffect(() => {
    if (isOpen) setValues(valoresIniciais(contato));
  }, [isOpen, contato]);

  const set = <K extends keyof ContatoFormValues>(campo: K, valor: ContatoFormValues[K]) =>
    setValues((prev) => ({ ...prev, [campo]: valor }));

  const invalido =
    (parte === "empregador" && !values.nome.trim()) ||
    values.telefone.length !== TELEFONE_MASCARADO_LEN ||
    !values.email.trim();

  const handleSubmit = async () => {
    if (invalido) return;
    await onSubmit(values);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {edicao ? `Editar contato do ${rotuloParte}` : `Adicionar contato do ${rotuloParte}`}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Flex flexDirection="column" gap={4}>
            {parte === "empregador" && (
              <FormControl isRequired>
                <FormLabel fontSize="sm">Nome</FormLabel>
                <Input
                  type="text"
                  value={values.nome}
                  onChange={(e) => set("nome", e.target.value)}
                  placeholder="Nome completo"
                  fontSize="sm"
                  maxLength={150}
                />
              </FormControl>
            )}

            <FormControl isRequired>
              <FormLabel fontSize="sm">Telefone</FormLabel>
              <Input
                type="tel"
                value={values.telefone}
                onChange={(e) => set("telefone", validateAndFormatPhone(e.target.value).formatted)}
                placeholder="Telefone"
                fontSize="sm"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Email</FormLabel>
              <Input
                type="email"
                value={values.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="Email"
                fontSize="sm"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Contato Quente</FormLabel>
              <Select
                value={values.temperatura}
                onChange={(e) => set("temperatura", e.target.value as ContatoTemperaturaEnum)}
                fontSize="sm"
              >
                <option value={ContatoTemperaturaEnum.QUENTE}>Quente</option>
                <option value={ContatoTemperaturaEnum.FRIO}>Frio</option>
              </Select>
            </FormControl>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} size="sm" isDisabled={isSaving}>
            Cancelar
          </Button>
          <Button
            colorScheme="primary"
            onClick={handleSubmit}
            size="sm"
            isDisabled={invalido}
            isLoading={isSaving}
          >
            {edicao ? "Salvar" : "Adicionar"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
